import type { IndicateurControle } from '@rosetta/shared';
import type { ReactNode } from 'react';
import { MentionStatut } from './mention-statut';

/**
 * The look of a card of the tab, and of its highlight when a counter leads to it (an outline: the
 * card keeps its size). The card of the changes (#30) has the same.
 */
export const CLASSES_CARTE =
  'mt-3 scroll-mt-6 rounded-md border border-gray-200 bg-white px-6 py-4 data-[evidence=true]:outline-3 data-[evidence=true]:outline-bleu-nuit data-[evidence=true]:outline-offset-2';

/** `id` of the card of an indicator: the target of its link in "Points à regarder". */
export const idCarteIndicateur = (numero: string): string =>
  `indicateur-${numero.replace(/[^0-9a-z]/gi, '-')}`;

interface CarteIndicateurProps {
  indicateur: IndicateurControle;
  /** Shows the `resume` of Tahiti under the title (the layout card writes its own line). */
  avecResume?: boolean;
  /** A sentence of ours in place of the `resume` of Tahiti (built from its numbers). */
  resume?: string | undefined;
  children?: ReactNode;
}

/**
 * The card of one indicator of the control (A8): its name as Tahiti gives it, its status on the
 * right, the summary of Tahiti under them, then what the indicator details. White, thin frame:
 * the colour is the status's, on its mention only. The numbering of Tahiti is not shown. The
 * card can take the focus (`tabIndex={-1}`), for the links of "Points à regarder".
 */
export function CarteIndicateur({
  indicateur,
  avecResume = true,
  resume,
  children,
}: CarteIndicateurProps) {
  const id = idCarteIndicateur(indicateur.numero);
  return (
    <article id={id} tabIndex={-1} aria-labelledby={`${id}-titre`} className={CLASSES_CARTE}>
      <div className="flex items-baseline justify-between gap-4">
        <h4 id={`${id}-titre`} className="font-bold text-body leading-[1.35]">
          {indicateur.nom}
        </h4>
        <MentionStatut statut={indicateur.statut} />
      </div>
      {avecResume && (resume ?? indicateur.resume) !== undefined ? (
        <p className="mt-1 text-gray-700 text-small leading-normal">
          {resume ?? indicateur.resume}
        </p>
      ) : null}
      {children}
    </article>
  );
}
